import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { formattedDate, cleanString, dateWithAsterisk } from "../utils";
import { useGetReportInfoListQuery } from "../api/ReportsApi";
import Table from "./Table";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  ReportSummaryApi,
  ReportSummaryItemApi,
  WORKFLOW_REPORT_STATUS_ENUM,
} from "../api/Reports";

import Pagination from "../shared-components/Pagination";
import { useAppContext } from "../AppProviders";
import { CacheKeys } from "../api/Cache";
import { LoadedSince } from "../shared-components/LoadedSince";
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import { genericErrorLoadingMessage } from "./utils";
import { useFamaFlags } from "../api/FamaFlags";
import ReactTooltip from "react-tooltip";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";

const ReportsInProgress = () => {
  const { search } = useLocation();
  const { refreshCache } = useAppContext();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const { canViewAllReportsWithinCompany } = usePermissionValidator();
  const { enableProductReportsForClientTab } = useFamaFlags();
  const pageSize = 25;
  const reportListInput = useMemo(
    () => ({
      person_name: filter,
      page_size: pageSize,
      page: page - 1,
      workflow_status: [WORKFLOW_REPORT_STATUS_ENUM.IN_PROGRESS],
    }),
    [filter, page]
  );
  const {
    isLoading: loadingReports,
    data: reportsData,
    refetch,
    error: reportsError,
  } = useGetReportInfoListQuery(
    reportListInput,
    canViewAllReportsWithinCompany,
    enableProductReportsForClientTab
  );

  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");
  useEffect(() => {
    setSearch(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);

  if (reportsError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  function clearCache() {
    refreshCache(CacheKeys.reports);
    refetch();
  }

  const convertReportsInProcessToRows = (
    reportsData: ReportSummaryApi | undefined
  ): ReportRowItem<ReportSummaryItemApi>[] => {
    if (!reportsData) return [];
    const reportRows: ReportRowItem<ReportSummaryItemApi>[] =
      reportsData.report_summaries.map((report: ReportSummaryItemApi) => {
        const data: ReportEntry[] = [
          {
            text: report.full_name,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <div className="flex">
                <div>
                  <span className="font-semibold">{report.full_name}</span>
                  <small className="block font-normal">
                    {report.company_name}
                  </small>
                </div>
                <div>
                  {(report.step_payload?.rerun_from_parent_uuid ||
                    report.has_rerun) && (
                    <div>
                      <ArrowPathIcon className="w-5 ml-2 text-blue-600" />
                    </div>
                  )}
                </div>
              </div>
            ),
          },
          {
            component: (
              <div className="flex flex-col">
                <span className="border-b-2">{report.blueprint_name}</span>{" "}
                <small className="block">
                  {report.behavior_kit_name ?? "General"}
                </small>
              </div>
            ),
            text:
              report.blueprint_name +
              " " +
              (report.behavior_kit_name ?? "General"),
            style: ReportEntryStyle.COMPONENT,
          },
          {
            text: formattedDate(report.report_date_added),
            style: ReportEntryStyle.COMPONENT,
            component: report.recur ? (
              <>
                <span>{formattedDate(report.report_date_added)}</span>
                <span data-tip="Employee Screening" className="cursor-pointer">
                  <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
                </span>
                <ReactTooltip effect="solid" />
              </>
            ) : (
              <div>{formattedDate(report.report_date_added)}</div>
            ),
            customClass: "pl-8 flex-1",
          },
          {
            text: formattedDate(report.due_date),
            style: ReportEntryStyle.COMPONENT,
            component: dateWithAsterisk(
              report.due_date,
              report.due_date_updated,
              "longForceUTCToLocalTime"
            ),
            customClass: "w-48",
          },
          {
            text: report.report_uuid,
            style: ReportEntryStyle.COMPONENT,
            component: (
              <div className="flex gap-1">
                <ClipboardIcon
                  onClick={() => {
                    navigator.clipboard.writeText(report.report_uuid);
                  }}
                  className="w-4 cursor-pointer"
                />
                <span className="w-16 h-[30px]">
                  {report.report_uuid.slice(0, 7)}
                </span>
              </div>
            ),
          },
          {
            text: `${report.client_report_id}`,
            style: ReportEntryStyle.TEXT,
          },
        ];

        return {
          report: report,
          report_uuid: report.report_uuid,
          isLocked: report.is_locked,
          data: data,
        };
      });
    return reportRows;
  };

  function buildHeader() {
    return [
      "Name / Company",
      "Check Type / Behavior Kit",
      { text: "Date Added", customClass: "pl-8 flex-1" },
      { text: "Date Due", customClass: "w-48" },
      "Report ID",
      "Client Reference ID",
    ];
  }

  return (
    <>
      <LoadedSince reportData={reportsData} onClear={clearCache} />
      <Table
        isLoading={loadingReports}
        header={buildHeader()}
        data={convertReportsInProcessToRows(reportsData)}
      />

      {!loadingReports && reportsData && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={reportsData.total_rows}
          search={filter}
        />
      )}
    </>
  );
};

export default ReportsInProgress;
